import React, {useContext, useEffect, useMemo, useState} from 'react'
import {connect} from 'react-redux'
import {createStructuredSelector} from 'reselect'
import {getActive} from 'clubs/selectors'
import styled from 'styled-components'
import {getDay} from 'date-fns'
import {compose, branch, renderNothing} from 'recompose'
import {Modal, Text, Button, Flex, Box} from 'components'
import {Clock, Pin, Payment} from 'components/icons'
import {getActivity, getActivityTypeEntities} from 'activities/selectors'
import {fetchStatistics, fetchActivityTypes} from 'activities/actions'
import format from 'lib/format'
import price from 'lib/price'
import Images from './Images'
import Comments from './Comments'
import Participants from './Participants'
import CreatedBy from './CreatedBy'
import Context from '../../Context'
import DeleteButton from './DeleteButton'
import CoHosts from './CoHosts'
import ValidURL from 'lib/validURL/ValidURL'
import useCustomTranslation from 'lib/customT'
import TransWrapper from 'lib/transWrapper'
import { getActivities } from 'activities/selectors'
import { getMembers } from 'members'
import { getGroups } from 'groups'

const HeaderImage = styled(Flex).attrs({
  flexDirection: 'column',
})`
  height: 120px;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
`

const StyledPre = styled.pre`
  white-space: pre-wrap;
  font-family: inherit;
  margin: 0;
`;

const ShowModal = ({id, activity, hide, toggleEdit, fetchStatistics, members, groups,
  club, fetchActivityTypes, activityTypes
}) => {
  const t = useCustomTranslation()
  const [isFetching, setIsFetching] = useState(true)
  const {showCreateModal, archived} = useContext(Context)
  const [reload, setReload] = useState(false)

  useEffect(() => {
    new Promise((resolve, reject) => {
      fetchStatistics({resolve, reject, id, archived})
    }).then(() => {
      setIsFetching(false)
      
    })

    new Promise((resolve, reject) => {
      fetchActivityTypes({
        values: {clubId: club.id},
        resolve,
        reject
      })
    }).then(() => {
      setReload(true)
    }).catch(error => {
      console.log(error)
    });

  }, [fetchStatistics, reload, fetchActivityTypes, club, setIsFetching, id, archived, members, groups])
 
  let backgroundHexColorLightTheme = '#ccc';
  let iconUrl = '';
  let activityType = null;

  if (activityTypes.length > 0) {
    let typeId = activity.type;
    try {
      if ('clubActivityTypeId' in typeId) {
        typeId = typeId.clubActivityTypeId
      }
    } catch {}
    
    activityType = activityTypes.filter(t => t.clubActivityTypeId == typeId)[0];
    
    if (activityType != null && 'backgroundHexColorLightTheme' in activityType) {
      backgroundHexColorLightTheme = activityType.backgroundHexColorLightTheme;
    }

    if (activityType != null && 'iconUrl' in activityType) {
      iconUrl = activityType.iconUrl;
    }
  }

  return activityTypes != null && activityTypes.length > 0 ? (
    <Modal hide={hide} title={activity.title} width={450} color={backgroundHexColorLightTheme}>
      {activity.coverImage && (
        <HeaderImage style={{backgroundImage: `url('${activity.coverImage}')`}} />
      )}

      <Box p={3}>
        <Flex>
          <Box mr={2}>
            <Clock size={16} fill={backgroundHexColorLightTheme} />
          </Box>
          <Text small {...{[backgroundHexColorLightTheme]: true}}>
            {format(activity.start, 'ddd D. MMM. HH:mm')}
            {activity.end &&
              ` - ${format(
                activity.end,
                getDay(activity.start) === getDay(activity.end)
                  ? 'HH:mm'
                  : 'ddd D. MMM. HH:mm'
              )}`}
          </Text>
        </Flex>
        {activity.deadline && (
          <Text {...{[backgroundHexColorLightTheme]: true}}>
            <small>
              {t('Svarfristen er {{date}}', {
                date: format(activity.deadline, 'ddd D MMM HH:mm'),
              })}
            </small>
          </Text>
        )}

        {activity.location && (
          <Flex mt={3}>
            <Box mr={2}>
              <Pin size={16} fill={backgroundHexColorLightTheme} />
            </Box>
            <Text small {...{[backgroundHexColorLightTheme]: true}}>
              <ValidURL string={activity.location}/>
            </Text>
          </Flex>
        )}

        {activity.activityPayment && (
          <Flex mt={3}>
            <Box mr={2}>
              <Payment size={18} fill={backgroundHexColorLightTheme} />
            </Box>
            <Text small {...{[backgroundHexColorLightTheme]: true}}>
              {price(activity.activityPayment.amount)}
            </Text>
          </Flex>
        )}

        {activity.description && (
          
          <Box mt={3}>
            <Text>
                <StyledPre><ValidURL string={activity.description}/></StyledPre>
            </Text>
          </Box>
        )}

        <CreatedBy
          id={activity.responsibleUserId || activity.createdByUserId}
          color={backgroundHexColorLightTheme}
        />

        <CoHosts coHosts={activity.coHosts} color={backgroundHexColorLightTheme} />
      </Box>

      {activity.images && activity.images.length > 0 && (
        <Images images={activity.images} />
      )}
      {((activity.users && activity.users.length > 0) ||
        (activity.groups && activity.groups.length > 0)) &&
        (isFetching ? null : (
          <Participants
            activity={activity}
            invitedCount={activity.invitedCount}
            participatingCount={activity.participatingCount}
            participationPercentage={activity.participationPercentage}
            participants={activity.users}
            groups={activity.groups}
            color={backgroundHexColorLightTheme}
            responsibleUserId={activity.responsibleUserId || activity.createdByUserId}
            fetchStatistics={fetchStatistics}
            archived={archived}
          />
        ))}

      {!activity.commentsDisabled && <Comments activityId={activity.id} />}

      <Flex p={3}>
        {!archived && (
          <Box flex="1" mr={3}>
            <Button primary block small onClick={toggleEdit}>
              {t('Rediger')}
            </Button>
          </Box>
        )}
        {!activity.activityPayment && (
          <Box flex="1" mr={3}>
            <Button
              secondary
              block
              small
              onClick={() => showCreateModal(activity.id)}
            >
              {t('Kopier')}
            </Button>
          </Box>
        )}
        <Box flex="1">
          <DeleteButton
            id={id}
            archived={archived}
            isRecurring={activity.isRecurring}
          >
            {t('Slet')}
          </DeleteButton>
        </Box>
      </Flex>
    </Modal>
  ) : <></>
}


const mapStateToProps = (state, { id }) => {
  // Using createStructuredSelector:
  const structured = createStructuredSelector({
    club: getActive,
    activityTypes: getActivityTypeEntities
  })(state)
  
  return {
    ...structured,
    activity: getActivity(state, id),
    activities: getActivities(state),
    members: getMembers(state),
    groups: getGroups(state)
  }
}

const enhancer = compose(
  connect(
    mapStateToProps,
    {
      fetchStatistics: fetchStatistics.requested,
      fetchActivityTypes: fetchActivityTypes.requested,
    }
  ),
  branch(({activity}) => !activity, renderNothing)
)

export default enhancer(ShowModal)
